import { useId, useState } from 'react'
import { Button, Checkbox, Input, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import type { WidgetProps } from './types'

interface Question {
  question: string
  options: string[]
  multiSelect?: boolean
}

interface Answer {
  selected: string[]
  /** Null until "Other" is chosen. */
  other: string | null
}

// Part of the outcome size budget set by the ask_questions tool schema.
const MAX_OTHER_CHARS = 100

const isQuestion = (value: unknown): value is Question =>
  typeof value === 'object' &&
  value !== null &&
  'question' in value &&
  typeof value.question === 'string' &&
  'options' in value &&
  Array.isArray(value.options) &&
  value.options.every((option) => typeof option === 'string') &&
  (!('multiSelect' in value) ||
    value.multiSelect === undefined ||
    typeof value.multiSelect === 'boolean')

const toQuestions = (input: Record<string, unknown>): Question[] =>
  Array.isArray(input.questions) ? input.questions.filter(isQuestion) : []

const isAnswered = ({ selected, other }: Answer): boolean =>
  selected.length > 0 || Boolean(other?.trim())

const formatAnswer = ({ selected, other }: Answer): string =>
  [...selected, ...(other?.trim() ? [`Other: "${other.trim()}"`] : [])].join(
    ', '
  )

const toOutcome = (questions: Question[], answers: Answer[]): string =>
  questions
    .map((q, i) => `Q: ${q.question} A: ${formatAnswer(answers[i])}`)
    .join('\n')

const AnswerButton = ({
  label,
  pressed,
  disabled,
  onClick,
}: {
  label: string
  pressed: boolean
  disabled: boolean
  onClick: () => void
}) => (
  <Button
    variant="secondary"
    aria-pressed={pressed}
    disabled={disabled}
    onClick={onClick}
    className={mtc(
      'body-sm h-auto w-full justify-start py-2.5 text-left font-normal whitespace-normal',
      pressed && 'border-neutral-700 font-medium text-neutral-900'
    )}
  >
    {label}
  </Button>
)

export const AskQuestionsWidget = ({
  input,
  resolved,
  onOutcome,
}: WidgetProps) => {
  const id = useId()
  const questions = toQuestions(input)
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>(() =>
    questions.map(() => ({ selected: [], other: null }))
  )

  if (questions.length === 0) return null

  const question = questions[step]
  const answer = answers[step]
  const isLast = step === questions.length - 1
  const showPrimary =
    Boolean(question.multiSelect) || answer.other !== null || isAnswered(answer)

  const withAnswer = (next: Answer): Answer[] =>
    answers.map((a, i) => (i === step ? next : a))

  const advance = (nextAnswers: Answer[]) =>
    isLast ? onOutcome(toOutcome(questions, nextAnswers)) : setStep(step + 1)

  const pick = (label: string) => {
    const nextAnswers = withAnswer({ selected: [label], other: null })
    setAnswers(nextAnswers)
    advance(nextAnswers)
  }

  const setAnswer = (next: Answer) => setAnswers(withAnswer(next))

  const toggle = (label: string, checked: boolean) =>
    setAnswer({
      ...answer,
      selected: checked
        ? [...answer.selected, label]
        : answer.selected.filter((s) => s !== label),
    })

  const otherInput = answer.other !== null && (
    <Input
      aria-label="Your answer"
      placeholder="Type your answer"
      inputClassName="body-sm"
      maxLength={MAX_OTHER_CHARS}
      disabled={resolved}
      value={answer.other}
      onChange={(e) => setAnswer({ ...answer, other: e.target.value })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && isAnswered(answer)) advance(answers)
      }}
      className={question.multiSelect ? 'pl-6' : undefined}
    />
  )

  return (
    <div className="flex flex-col gap-3">
      {questions.length > 1 && (
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
          Question {step + 1} of {questions.length}
        </Text>
      )}
      <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
        {question.question}
      </Text>

      {question.multiSelect ? (
        <div className="flex flex-col gap-2">
          {question.options.map((option, i) => (
            <Checkbox
              key={option}
              id={`${id}-${step}-${i}`}
              label={option}
              disabled={resolved}
              checked={answer.selected.includes(option)}
              onCheckedChange={(checked) => toggle(option, checked)}
            />
          ))}
          <Checkbox
            id={`${id}-${step}-other`}
            label="Other"
            disabled={resolved}
            checked={answer.other !== null}
            onCheckedChange={(checked) =>
              setAnswer({ ...answer, other: checked ? '' : null })
            }
          />
          {otherInput}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {question.options.map((option) => (
            <AnswerButton
              key={option}
              label={option}
              pressed={answer.selected.includes(option)}
              disabled={resolved}
              onClick={() => pick(option)}
            />
          ))}
          <AnswerButton
            label="Other"
            pressed={answer.other !== null}
            disabled={resolved}
            onClick={() =>
              setAnswer({ selected: [], other: answer.other ?? '' })
            }
          />
          {otherInput}
        </div>
      )}

      {(step > 0 || showPrimary) && (
        <div className="flex gap-2">
          {step > 0 && (
            <Button
              size="sm"
              variant="secondary"
              disabled={resolved}
              onClick={() => setStep(step - 1)}
            >
              Previous
            </Button>
          )}
          {showPrimary && (
            <Button
              size="sm"
              className="ml-auto"
              disabled={resolved || !isAnswered(answer)}
              onClick={() => advance(answers)}
            >
              {isLast ? 'Submit' : 'Next'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
